import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Clock, Crown, Download, FileText, Loader2, MessageSquare, Users, Video, X } from "@fg/icons";
import { connectParityApi, type AttendanceRow } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useMeetingChatMessages } from "@/api/hooks";
import { splitFileMarkers, type FileRef } from "@/lib/fileMarkers";
import { cn } from "@/lib/utils";
import { ChatAvatar, timeLabel } from "../chat/chatUi";
import { FileAttachmentList } from "../files/DriveFileCard";
import { MessageBody } from "../files/MessageBody";
import { RecordingCard, useMeetingRecordings } from "../recording/MeetingRecordings";
import { WhiteboardViewer } from "../whiteboard/WhiteboardViewer";
import { MeetingNotes } from "./MeetingNotes";

type Section = "attendance" | "recordings" | "files" | "chat" | "notes" | "whiteboard";

/**
 * Teams' meeting Recap: what happened, when, and who was there. Date and
 * duration (first person in → last person out), the organizer, the attendance
 * list (each person's join / leave times and total time — the organizer can
 * download it as CSV), the recordings, the files shared in the meeting chat,
 * and the chat itself. Everything is read with the viewer's own access: the
 * recap is open to the organizer, invitees and anyone who joined.
 */
export function MeetingRecap({ meetingId, className }: { meetingId: string; className?: string }) {
    const token = useAccessToken();
    const [section, setSection] = useState<Section>("attendance");
    const recap = useQuery({
        queryKey: ["meeting-recap", meetingId, token],
        queryFn: () => connectParityApi.meetingRecap(meetingId, token),
        enabled: Boolean(token),
        staleTime: 30_000
    });
    const recordings = useMeetingRecordings(meetingId);
    const files = useQuery({
        queryKey: ["meeting-recap-files", meetingId, token],
        queryFn: () => connectParityApi.listConversationFiles("meetings", meetingId, null, token),
        enabled: Boolean(token),
        staleTime: 30_000
    });
    const chat = useMeetingChatMessages(meetingId);

    const sharedFiles = useMemo<FileRef[]>(() => {
        const seen = new Set<string>();
        const out: FileRef[] = [];
        for (const m of files.data?.messages ?? []) {
            for (const f of splitFileMarkers(m.markersHtml).files) {
                const key = f.id.toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                out.push(f);
            }
        }
        return out;
    }, [files.data]);

    const [downloading, setDownloading] = useState(false);
    const [csvError, setCsvError] = useState<string | null>(null);
    const downloadCsv = async () => {
        if (!recap.data) return;
        setDownloading(true);
        setCsvError(null);
        try {
            const res = await fetch(connectParityApi.meetingAttendanceCsvUrl(meetingId), {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: "omit"
            });
            if (!res.ok) throw new Error(res.status === 403 ? "Only the organizer can download attendance." : `Download failed (${res.status})`);
            const url = URL.createObjectURL(await res.blob());
            try {
                const a = document.createElement("a");
                a.href = url;
                a.download = `${recap.data.meeting.subject.replace(/[^a-z0-9_\- ]/gi, "_").slice(0, 60) || "meeting"} - attendance.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } finally {
                URL.revokeObjectURL(url);
            }
        } catch (err) {
            setCsvError(err instanceof Error ? err.message : "Download failed");
        } finally {
            setDownloading(false);
        }
    };

    if (recap.isPending) {
        return (
            <p className={cn("flex items-center gap-2 text-sm text-muted-foreground", className)}>
                <Loader2 className="h-4 w-4 animate-spin" /> Loading recap…
            </p>
        );
    }
    if (recap.isError || !recap.data) {
        return <p role="alert" className={cn("text-sm text-destructive", className)}>Couldn't load the recap.</p>;
    }

    const r = recap.data;
    const when = new Date(r.firstJoinAt ?? r.meeting.actualStart ?? r.meeting.scheduledStart);
    const live = r.attendance.length > 0 && !r.lastLeaveAt;
    const organizer = r.isOrganizer ? "You" : r.organizerName || r.organizerEmail || "The organizer";
    const recs = recordings.data ?? [];
    const messages = chat.data ?? [];

    const sections: Array<{ id: Section; label: string; count: number | null }> = [
        { id: "attendance", label: "Attendance", count: r.attendance.length },
        { id: "recordings", label: "Recordings", count: recordings.isPending ? null : recs.length },
        { id: "files", label: "Files", count: files.isPending ? null : sharedFiles.length },
        { id: "chat", label: "Chat", count: chat.isPending ? null : messages.length },
        { id: "notes", label: "Notes", count: null },
        { id: "whiteboard", label: "Whiteboard", count: null }
    ];

    return (
        <div className={cn("space-y-3", className)}>
            <div className="space-y-1.5">
                <h2 className="break-words text-base font-semibold">{r.meeting.subject}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3.5 w-3.5" aria-hidden />
                        {when.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })} · {timeLabel(when)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" aria-hidden />
                        {r.attendance.length === 0 ? "Nobody joined" : `${formatSpan(r.durationSeconds)}${live ? " so far" : ""}`}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <Crown className="h-3.5 w-3.5" aria-hidden /> Organized by {organizer}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" aria-hidden /> {r.attendance.length} attended
                    </span>
                </div>
            </div>

            <div role="tablist" aria-label="Recap sections" className="flex flex-wrap gap-1 border-b pb-1.5">
                {sections.map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        role="tab"
                        aria-selected={section === s.id}
                        onClick={() => setSection(s.id)}
                        className={cn(
                            "h-7 rounded-full px-2.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            section === s.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"
                        )}
                    >
                        {s.label}{s.count !== null ? ` · ${s.count}` : ""}
                    </button>
                ))}
            </div>

            {section === "attendance" ? (
                <section aria-label="Attendance" className="space-y-2">
                    {r.isOrganizer && r.attendance.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => void downloadCsv()}
                                disabled={downloading}
                                className="inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-accent disabled:opacity-50"
                            >
                                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                Download attendance (CSV)
                            </button>
                            {csvError ? <span role="alert" className="text-xs text-destructive">{csvError}</span> : null}
                        </div>
                    ) : null}
                    {r.attendance.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nobody has joined this meeting yet.</p>
                    ) : (
                        <ul className="divide-y rounded-md border">
                            {r.attendance.map((a) => <AttendanceItem key={a.key} row={a} />)}
                        </ul>
                    )}
                </section>
            ) : section === "recordings" ? (
                <section aria-label="Recordings" className="space-y-1.5">
                    {recordings.isPending && recordings.fetchStatus === "fetching" ? (
                        <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading recordings…</p>
                    ) : recs.length === 0 ? (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground"><Video className="h-4 w-4" aria-hidden /> This meeting wasn't recorded.</p>
                    ) : recs.map((rec) => <RecordingCard key={rec.id} recording={rec} className="max-w-none" />)}
                </section>
            ) : section === "files" ? (
                <section aria-label="Files shared in the meeting chat">
                    {files.isPending ? (
                        <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading files…</p>
                    ) : sharedFiles.length === 0 ? (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground"><FileText className="h-4 w-4" aria-hidden /> No files were shared in the meeting chat.</p>
                    ) : (
                        <FileAttachmentList files={sharedFiles} />
                    )}
                </section>
            ) : section === "notes" ? (
                <section aria-label="Meeting notes">
                    <MeetingNotes meetingId={meetingId} />
                </section>
            ) : section === "whiteboard" ? (
                <section aria-label="Whiteboard">
                    <WhiteboardViewer meetingId={meetingId} meetingSubject={r.meeting.subject} />
                </section>
            ) : (
                <section aria-label="Chat transcript">
                    {chat.isPending ? (
                        <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Loading chat…</p>
                    ) : messages.length === 0 ? (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground"><MessageSquare className="h-4 w-4" aria-hidden /> No messages in the meeting chat.</p>
                    ) : (
                        <ol className="max-h-96 space-y-2 overflow-y-auto rounded-md border p-2">
                            {messages.map((m) => {
                                const name = m.authorDisplayName || m.authorEmail || (m.isGuest ? "Guest" : "Someone");
                                const at = new Date(m.sentAt);
                                return (
                                    <li key={m.id} className="flex gap-2">
                                        <ChatAvatar name={name} size="sm" />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-baseline gap-2 text-[11px] text-muted-foreground">
                                                <span className="font-semibold text-foreground">{name}</span>
                                                <time dateTime={m.sentAt} title={at.toLocaleString()}>{at.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</time>
                                                {m.isEdited ? <span>(edited)</span> : null}
                                            </div>
                                            <MessageBody html={m.bodyHtml} className="break-words text-sm [&_a]:text-primary [&_a]:underline" />
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </section>
            )}
        </div>
    );
}

function AttendanceItem({ row }: { row: AttendanceRow }) {
    const [open, setOpen] = useState(false);
    const first = new Date(row.firstJoinedAt);
    const last = row.lastLeftAt ? new Date(row.lastLeftAt) : null;
    const role = row.isOrganizer ? "Organizer" : row.isGuest ? "Guest" : row.invited ? "Invitee" : "Attendee";
    return (
        <li className="px-3 py-2">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="flex w-full items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <ChatAvatar name={row.displayName} size="sm" />
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{row.displayName}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                        {role}{row.email ? ` · ${row.email}` : ""}
                    </span>
                </span>
                <span className="shrink-0 text-right text-[11px] text-muted-foreground">
                    <span className="block font-medium text-foreground">{formatSpan(row.totalSeconds)}</span>
                    <span className="block">{timeLabel(first)} – {last ? timeLabel(last) : "now"}</span>
                </span>
            </button>
            {open ? (
                <ol className="ml-10 mt-1.5 space-y-0.5 text-[11px] text-muted-foreground">
                    {row.sessions.map((s, i) => {
                        const j = new Date(s.joinedAt);
                        const l = s.leftAt ? new Date(s.leftAt) : null;
                        return (
                            <li key={`${s.joinedAt}-${i}`}>
                                Joined {j.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", second: "2-digit" })}
                                {" · "}
                                {s.live ? "still here" : l ? `left ${l.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" })}` : "left (time unknown)"}
                            </li>
                        );
                    })}
                </ol>
            ) : null}
        </li>
    );
}

/** A recap in its own dialog — from Call history. */
export function MeetingRecapDialog({ meetingId, onClose }: { meetingId: string; onClose: () => void }) {
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Meeting recap"
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        >
            <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border bg-card shadow-2xl">
                <div className="flex items-center justify-between border-b px-5 py-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recap</span>
                    <button type="button" onClick={onClose} autoFocus className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent" aria-label="Close recap">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <MeetingRecap meetingId={meetingId} className="px-5 py-4" />
            </div>
        </div>
    );
}

/** 3725 s → "1h 2m", 125 → "2m 5s". */
export function formatSpan(seconds: number): string {
    const s = Math.max(0, Math.round(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
}
